<template lang="html">
  <div>
    <reservations-item
      v-model="selectedItemId"
      :clone-id="cloneId"
    />
    <v-card
      v-if="!showItemOnly"
      :key="instanceKey"
    >
      <page-bar
        :title="pageTitle"
        :context-menu="reservationListContextMenu"
        :loading="isDataLoading"
        :buttons="reservationListPageBarButtons"
      />

      <api-errors :errors="errors" />

      <!-- MEMBER PREFERENCES DIALOG -->
      <member-preferences
        v-model="selectedMemberPreferencesId"
        @change="onMemberPreferenceChange"
      />
      <reservation-no-show
        v-model="selectedNoShowId"
        @close="refreshList"
      />

      <!-- LIST OF ITEMS -->
      <item-select-table
        :add-action="addNewItem"
        :add-label="'Add ' + type"
        :read-only="!hasAddAccess"
        :edit-action="editItem"
        :items="items"
        :pagination.sync="pagination"
        :total-items="totalListItems"
        expand
        :expand-icon="getListExpandIcon"
        :expand-color="getListExpandIconColor"
        :headers="headers"
        :row-class="itemRowClass"
      >
        <template slot="search-filters">
          <!-- SEARCH FILTERS -->
          <v-layout wrap>
            <v-flex class="xs12 sm6 md6 px-3">
              <!-- FILTER BY RESERVATION # / DATE -->
              <v-layout>
                <v-flex
                  class="pr-3"
                  style="max-width: 120px"
                >
                  <!-- FILTER BY RESERVATION # -->
                  <v-text-field
                    v-model="searchFilter.reservationNumber"
                    label="Res. #"
                    clearable
                  />
                </v-flex>
                <v-flex>
                  <!-- FILTER BY DATE -->
                  <v-autocomplete
                    v-if="searchFilter.eventName"
                    v-model.trim="searchFilter.date"
                    auto
                    label="Event Date"
                    prepend-icon="mdi-calendar"
                    :items="selectedEventDates"
                    clearable
                    @click:clear="$nextTick(() => searchFilter.eventName = null)"
                  />
                  <date-picker
                    v-else
                    v-model.trim="searchFilter.date"
                    label="Date"
                    clearable
                    :min="minFilterDate"
                    :max="maxFilterDate"
                    :allowed-dates="allowedFilterDates"
                  />
                </v-flex>
              </v-layout>

              <!-- FILTER BY MEMBER / GUEST -->
              <v-layout>
                <v-flex class="pr-2">
                  <v-autocomplete
                    v-model="searchFilter.guestTypeId"
                    auto
                    label="Type"
                    :items="modGuestTypes"
                    item-text="name"
                    item-value="id"
                  />
                </v-flex>
                <v-flex>
                  <!-- FILTER BY MEMBER -->
                  <member-select
                    v-if="!searchFilter.guestTypeId"
                    v-model="searchFilter.memberId"
                    find-member-on-load
                    label="Member Search"
                    :error-handler="apiResourceError"
                    enable-info-select
                    @info-select="openMemberPreferences"
                  />
                  <!-- FILTER BY GUEST NAME -->
                  <v-text-field
                    v-else-if="!(guestTypeMap[searchFilter.guestTypeId] || {}).isTbd"
                    v-model="searchFilter.guestSearch"
                    label="Guest Search"
                    clearable
                  />
                  <!-- TBD -->
                  <v-text-field
                    v-else
                    disabled
                    label="All TBD"
                    :value="null"
                  />
                </v-flex>
              </v-layout>


              <!-- FILTER BY ORIGINATOR TYPE -->
              <v-autocomplete
                v-model="searchFilter.origByField"
                label="Originated By"
                prepend-icon="mdi-account-network"
                auto
                :items="originatedBySelectList"
                clearable
                item-text="name"
                item-value="field"
                @click:clear="$nextTick(() => searchFilter.origByField = null)"
              />


              <!-- FILTER BY CONFIRMATION STATUS -->
              <v-autocomplete
                v-model="searchFilter.confirmationStatusId"
                label="Confirmation Status"
                prepend-icon="mdi-phone"
                auto
                :items="confirmationStatuses"
                clearable
                item-text="name"
                item-value="id"
                @click:clear="$nextTick(() => searchFilter.confirmationStatusId = null)"
              />


              <!-- ORDER OF FILTERED RESULTS -->
              <v-autocomplete
                v-model="searchFilter.listingSortOrderId"
                label="Sort Order"
                prepend-icon="mdi-sort"
                auto
                :items="listingSortOrder"
                item-text="name"
                item-value="id"
              />
            </v-flex>
            <v-flex class="xs12 sm6 md6 px-3">
              <!-- FILTER BY EVENT NAME -->
              <v-autocomplete
                v-if="clubUsesEvents"
                v-model="searchFilter.eventName"
                label="Event"
                prepend-icon="mdi-calendar"
                clearable
                :items="distinctEventNames"
                item-text="name"
                item-value="name"
                @click:clear="$nextTick(() => searchFilter.eventName = null)"
              />


              <!-- FILTER BY MEAL PERIOD -->
              <v-autocomplete
                v-if="!selectedEventsCostsFilter.length && clubUsesMealPeriods"
                v-model="searchFilter.mealPeriodIds"
                label="Meal Period"
                prepend-icon="mdi-silverware-fork-knife"
                :items="mealPeriods"
                multiple
                clearable
                item-text="name"
                item-value="id"
                chips
                autocomplete
              >
                <template
                  slot="selection"
                  slot-scope="data"
                >
                  <v-chip
                    :key="JSON.stringify(data.item)"
                    :input-value="data.selected"
                    class="chip--select-multi"
                    @input="data.parent.selectItem(data.item)"
                    @click="data.parent.selectItem(data.item)"
                  >
                    {{ data.item.name }}
                  </v-chip>
                </template>

                <template v-slot:item="{ active, item, attrs, on }">
                  <v-list-item
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-list-item-action>
                      <v-checkbox :input-value="attrs.inputValue" />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle>{{ item.locationName }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-icon>
                      <v-tooltip
                        v-if="item.overlappingIds.length"
                        left
                      >
                        <template v-slot:activator="tt">
                          <v-btn
                            icon
                            v-bind="tt.attrs"
                            v-on="tt.on"
                            @click.stop="toggleMealPeriods(item.overlappingIds)"
                          >
                            <v-icon>mdi-vector-link</v-icon>
                          </v-btn>
                        </template>
                        <span>Overlaps Other Meal Period(s)</span>
                      </v-tooltip>
                    </v-list-item-icon>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <!-- OR FILTER BY EVENT PRICE CATEGORY -->
              <v-autocomplete
                v-else
                v-model="searchFilter.eventCostName"
                label="Price Category"
                prepend-icon="mdi-currency-usd"
                :items="uniqueSelectedEventsCostsFilter"
                clearable
                item-text="name"
                item-value="name"
                @click:clear="$nextTick(() => searchFilter.eventCostName = null)"
              />


              <!-- FILTER BY LOCATION -->
              <v-autocomplete
                v-model="searchFilter.locationId"
                label="Location"
                prepend-icon="mdi-map-marker"
                :items="locations"
                item-disabled="a-non-existent-field"
                clearable
                item-text="name"
                item-value="id"
                @click:clear="$nextTick(() => searchFilter.locationId = null)"
              />

              <!-- FILTER BY DAY OF WEEK -->
              <days-switch
                ref="dayPicker"
                v-model="searchFilter.days"
                label="Days"
              />


              <!-- FILTER CANCELLED -->
              <v-boolean
                v-model="searchFilter.cancelled"
                label="Include Canceled"
              />
            </v-flex>
          </v-layout>
        </template>
        <template
          slot="id"
          slot-scope="props"
        >
          <div>
            <v-icon
              v-if="getReservationIdListIcon(props.item)"
              class="ft-reservation-id-icon"
              :color="getReservationIdListIconColor(props.item)"
            >
              {{ getReservationIdListIcon(props.item) }}
            </v-icon>
            <span>
              <div class="ft-res-id">{{ props.item.id }}</div>
              <v-icon
                v-if="props.item.waitlist"
                color="warning"
                small
              >mdi-pause-octagon</v-icon>
              <v-icon
                v-if="props.item.walkIn"
                small
              >mdi-walk</v-icon>
              <v-icon
                v-if="props.item.hasBirthday"
                small
              >mdi-cake-variant</v-icon>
            </span>
          </div>
        </template>
        <template
          slot="tableAssignments"
          slot-scope="props"
        >
          <div
            v-if="props.item.tableNumber"
            style="white-space: nowrap"
          >
            <span :class="$ftStyle.inlineCircleAltClass">#</span><span>{{ props.item.tableNumber }}</span>
          </div>
          <div
            v-for="(table, i) of props.item.tableAssignments"
            :key="i"
            style="white-space: nowrap"
          >
            <span :class="$ftStyle.inlineCircleClass">{{ table.quantity }}</span><span>{{ table.locationTableName }}</span>
            <div
              v-if="table.notes"
              class="caption"
              style="white-space: normal; font-size: 80% !important;"
            >
              - {{ table.notes }}
            </div>
          </div>
        </template>
        <template
          slot="eventName"
          slot-scope="props"
        >
          <v-icon class="ft-event-or-mp-icon">
            {{ props.item.eventId ? 'mdi-calendar' : 'mdi-silverware-fork-knife' }}
          </v-icon><span>{{ props.item.eventName || props.item.mealPeriodName }}</span>
        </template>
        <template
          slot="details"
          slot-scope="props"
        >
          <div
            v-for="(detail, i) of getReserveesForList(props.item)"
            :key="i"
            style="white-space: nowrap"
          >
            <span>{{ detail.name }}</span> <span v-if="detail.count > 1">({{ detail.count }})</span>
          </div>
        </template>
        <template
          slot="expand-content"
          slot-scope="props"
        >
          <v-container
            fluid
            grid-list-sm
            class="pa-1"
          >
            <v-layout wrap>
              <v-flex
                v-for="(item, i) of getExpandMemberList(props.item)"
                :key="i"
                xs12
                md6
                lg3
              >
                <reservee-card
                  :reservee="item"
                  :reservation="props.item"
                  @click:avatar="openMemberPreferences(item.member)"
                />
                <!-- <v-card height="100%">
                  <v-card-title class="px-2 py-1">
                    <v-list-item-avatar
                      :style="item.member.id ? 'cursor: pointer;': ''"
                      @click="openMemberPreferences(item.member)"
                    >
                      <img
                        v-if="item.memberPhoto"
                        :src="item.memberPhoto"
                      >
                      <v-icon v-else>
                        {{ item.icon }}
                      </v-icon>
                    </v-list-item-avatar>
                    <div>
                      <v-card-text class="pa-0 subheading">
                        {{ item.name }}
                      </v-card-text>
                      <v-card-text
                        v-if="item.member.memNum || sameMonthDayInt(props.item.dateTime, item.member.birth) || (item.guests && item.guests.length)"
                        class="pa-0 caption"
                      >
                        <v-icon
                          v-if="sameMonthDayInt(props.item.dateTime, item.member.birth)"
                          small
                        >
                          mdi-cake-variant
                        </v-icon>
                        <span v-if="item.member.memNum">{{ item.member.memNum }} ({{ item.member.mType }}) </span>
                        <span v-if="item.guests && item.guests.length">+ {{ item.guests.length }} Guest</span>
                      </v-card-text>
                    </div>
                  </v-card-title>
                  <v-card-text class="py-0 px-1 ft-chip-gap">
                    <v-chip
                      v-if="item.member.allergyInformation"
                      :color="$ftStyle.reservation.allergyColor"
                      class="mb-1 ft-expand-chip"
                      text-color="white"
                    >
                      <v-icon
                        left
                        class="mr-1 pl-1"
                      >
                        {{ $ftStyle.reservation.allergyIcon }}
                      </v-icon>
                      <span>{{ item.member.allergyInformation }}</span>
                    </v-chip>
                    <v-chip
                      v-if="item.member.foodPreference"
                      :color="$ftStyle.reservation.foodPreferenceColor"
                      class="mb-1 ft-expand-chip"
                      text-color="white"
                    >
                      <v-icon
                        left
                        class="mr-1 pl-1"
                      >
                        mdi-silverware
                      </v-icon>
                      <span>{{ item.member.foodPreference }}</span>
                    </v-chip>
                    <v-chip
                      v-if="item.member.drinkPreference"
                      :color="$ftStyle.reservation.drinkPreferenceColor"
                      class="mb-1 ft-expand-chip"
                      text-color="white"
                    >
                      <v-icon
                        left
                        class="mr-1 pl-1"
                      >
                        mdi-glass-cocktail
                      </v-icon>
                      <span>{{ item.member.drinkPreference }}</span>
                    </v-chip>
                  </v-card-text>
                  <v-card-text
                    v-if="item.member.diningSpecialRequests"
                    class="pa-2"
                  >
                    {{ item.member.diningSpecialRequests }}
                  </v-card-text>
                  <v-card-text
                    v-if="getExpandedGuests(item)"
                    class="pa-2"
                  >
                    Guests:
                  </v-card-text>
                  <v-card-text class="py-0 px-1">
                    <v-chip
                      v-for="(guest, i2) of getExpandedGuests(item)"
                      :key="i2"
                      class="ml-0 mr-1 ft-expand-chip"
                    >
                      <v-avatar
                        v-if="guest.count > 1"
                        class="grey lighten-1"
                      >
                        {{ guest.count }}
                      </v-avatar>
                      <template v-if="guest.name">
                        <strong>{{ guest.name }}</strong> <span class="ml-2 caption">({{ guest.typeName }})</span>
                      </template>
                      <span v-else>{{ guest.typeName }}</span>
                    </v-chip>
                  </v-card-text>
                </v-card> -->
              </v-flex>
            </v-layout>
          </v-container>
        </template>
        <template
          slot="covers"
          slot-scope="props"
        >
          <v-btn
            rounded
            outlined
            small
            :color="coversButtonColor(props.item)"
            dark
            class="ft-covers-btn"
            @click.stop="selectedNoShowId = props.item.id"
          >
            {{ (props.item.covers - props.item.noShows) }} / {{ props.item.covers }}
          </v-btn>
        </template>
      </item-select-table>
    </v-card>
  </div>
</template>

<script>
// TODO: Find and fix performance issues for large numbers of reservees
// import moment from 'moment'
import {itemSelect, withChildren} from '@/ft-vue-core'
import ReserveeCard from '@/ft-vue-core/components/ReserveeCard'
import reserveeCardMethods from './mixins/reserveeCardMethods'
import reservationCommonData from './mixins/reservationCommonData'
import reservationListData from './mixins/reservationListData'
import reservationListComputed from './mixins/reservationListComputed'
import reservationListProps from './mixins/reservationListProps'
import reservationsCommonMethods from './mixins/reservationsCommonMethods'
import reservationListMethods from './mixins/reservationListMethods'
import reservationListResources from './mixins/reservationListResources'
import reservationListWatchers from './mixins/reservationListWatchers'
import ReservationsItem from './ReservationsItem'
import ReservationNoShow from '@/components/common/ReservationNoShow'

export default {
  name: 'ReservationsList',
  components: {
    ReservationsItem,
    ReservationNoShow,
    ReserveeCard,
  },
  mixins: [
    itemSelect,
    withChildren,
    reserveeCardMethods,
    reservationCommonData,
    reservationListData,
    reservationListComputed,
    reservationListProps,
    reservationsCommonMethods,
    reservationListMethods,
    reservationListResources,
    reservationListWatchers
  ]
}
</script>
<style>
  .ft-event-or-mp-icon.v-icon.v-icon {
    font-size: 16px;
    margin-right: 8px;
  }
  .ft-time-avatar {
    width: auto !important;
    margin-left: 15px;
  }
  .ft-time-avatar .chip .chip__content {
    width: 90px;
    text-align: center;
    justify-content: center;
  }
  .ft-darken-hover:hover {
    filter: brightness(95%);
  }
  .ft-reservation-id-icon+span {
    position: relative
  }
  .ft-reservation-id-icon {
    position: absolute
  }
  .ft-cancelled-item {
    text-decoration: line-through
  }
  .ft-member-orig td {
    background-color: rgba(255, 255, 0, 0.09);
  }
  .ft-cancelled-event-option {
    text-decoration: line-through
  }
  .ft-covers-btn {
    min-width: 10px;
    margin: 0;
  }
  .ft-covers-btn .btn__content {
    padding: 0 5px;
    margin-top: -1px;
  }

</style>
